import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import SoundEffectActions, { SoundEffectSelectors } from "../Redux/SoundEffectRedux";
import { formatDuration } from "../services/soundEffects";
import Spinner from "./Spinner";

const DEFAULT_CATEGORIES = [
  'ambient', 'nature', 'music', 'ui', 'foley', 'impact',
  'whoosh', 'transition', 'voice', 'animal', 'weather', 'urban'
];

export default function SoundEffectSearch({ onSelect, disabled = false }) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [audioLoading, setAudioLoading] = useState(null);
  
  // Redux state
  const results = useSelector(SoundEffectSelectors.getSearchResults);
  const isLoading = useSelector(SoundEffectSelectors.isSearching);
  const error = useSelector(SoundEffectSelectors.getSearchError);
  const categories = useSelector(SoundEffectSelectors.getCategories);
  const isLoadingCategories = useSelector(SoundEffectSelectors.isCategoriesFetching);
  
  const audioRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    // Load categories
    dispatch(SoundEffectActions.soundEffectsCategoriesRequest());
    // Load initial results (backend returns random soothing sounds when no query)
    dispatch(SoundEffectActions.soundEffectsSearchRequest("", {}));
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [dispatch]);

  const debouncedSearch = useCallback((searchQuery, searchCategory) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      // Combine query with category for search
      // If category is selected, use it as the query or append to existing query
      let finalQuery = searchQuery;
      if (searchCategory && !searchQuery) {
        finalQuery = searchCategory;
      } else if (searchCategory && searchQuery) {
        finalQuery = `${searchQuery} ${searchCategory}`;
      }
      
      dispatch(SoundEffectActions.soundEffectsSearchRequest(finalQuery, {}));
    }, 300);
  }, [dispatch]);

  const handleQueryChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery, category);
  };

  const handleCategoryChange = (newCategory) => {
    const cat = newCategory === category ? null : newCategory;
    setCategory(cat);
    debouncedSearch(query, cat);
  };

  const playPreview = (sfx) => {
    if (!sfx.preview_url) return;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setPlayingId(null);
      audioRef.current.onerror = () => {
        setPlayingId(null);
        setAudioLoading(null);
      };
      audioRef.current.oncanplaythrough = () => setAudioLoading(null);
    }

    if (playingId === sfx.provider_sound_id) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingId(null);
    } else {
      setAudioLoading(sfx.provider_sound_id);
      audioRef.current.src = sfx.preview_url;
      audioRef.current.play()
        .then(() => {
          setPlayingId(sfx.provider_sound_id);
          setAudioLoading(null);
        })
        .catch((err) => {
          console.error("Audio playback failed:", err);
          setPlayingId(null);
          setAudioLoading(null);
        });
    }
  };

  const handleAddClick = (sfx) => {
    if (onSelect) {
      onSelect(sfx);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden="true">🔊</span>
          <h3 className="font-semibold text-gray-900">Sound Effects</h3>
        </div>
      </div>

      {/* Search Input */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Search sound effects..."
            disabled={disabled}
            className="w-full pl-10 pr-4 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 py-3 border-b border-gray-100 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {isLoadingCategories ? (
            <div className="text-sm text-gray-500">Loading categories...</div>
          ) : (
            categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                disabled={disabled}
                className={`px-3 py-2 text-sm rounded-full transition-colors whitespace-nowrap min-h-[36px] sm:min-h-0 ${
                  category === cat
                    ? "bg-primary-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="p-8 text-center flex flex-col items-center gap-2">
            <Spinner size="lg" />
            <p className="text-gray-500 text-sm">Searching...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-red-500 text-sm">{error}</p>
            <button
              onClick={() => debouncedSearch(query, category)}
              className="mt-2 text-sm text-primary-500 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : results.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No sound effects found. Try a different search.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {results.map((sfx) => (
              <div
                key={sfx.provider_sound_id}
                className="p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Play Button */}
                  <button
                    onClick={() => playPreview(sfx)}
                    disabled={disabled || !sfx.preview_url}
                    aria-label={playingId === sfx.provider_sound_id ? `Pause ${sfx.name}` : `Preview ${sfx.name}`}
                    className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      playingId === sfx.provider_sound_id
                        ? "bg-primary-100 text-primary-600"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {audioLoading === sfx.provider_sound_id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : playingId === sfx.provider_sound_id ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">
                        {sfx.name}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatDuration(sfx.duration)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {sfx.category && (
                        <span className="text-xs text-gray-500">{sfx.category}</span>
                      )}
                      {sfx.tags && sfx.tags.length > 0 && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-xs text-gray-400 truncate">
                            {sfx.tags.slice(0, 3).join(", ")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={() => handleAddClick(sfx)}
                    disabled={disabled}
                    className="px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 min-h-[44px] flex items-center"
                  >
                    + Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
